import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { apiAgent } from '../../../axiosConfig.js'
import { useToast } from '../../../design_system/components/Toast/ToastContext.jsx'
import { Loader } from '../../../design_system/components/Loader.jsx'
import { useSettingsContext, settingsSnapshot } from './SettingsContext'
import {
    NOTIFICATION_POST_TYPES,
    buildTypeSettings,
    findInvalidMentionedFor,
    toApiPayload,
} from '../../utils/AllNotificationOptions.js'
import NotificationTypeCard from './_NotificationTypeCard.jsx'

const ManageNotifications = (props) => {
    const current_team = useSelector(s => s.current_team.team)
    const toast = useToast();
    const { registerTab, unregisterTab, setHasChanges } = useSettingsContext()
    const snapshotRef = useRef(null)

    const [settings, setSettings] = useState(() => buildTypeSettings(null))
    const [mentionedForTags, setMentionedForTags] = useState({}) // per-type map
    const [enabledTypes, setEnabledTypes] = useState({}) // per-type boolean map
    const [expandedType, setExpandedType] = useState(null) // accordion: all closed at start, max one open
    const [isDataLoaded, setIsDataLoaded] = useState(false)

    useEffect(() => {
        props.setActiveSettingTab('notifications')
        apiAgent({
            method: 'get',
            url: '/users/get_notifications',
            params: {}
        }).then(res => {
            const built = buildTypeSettings(res.data.notification_settings)
            setSettings(built)
            setMentionedForTags(res.data.mentioned_for_tags || {})
            setEnabledTypes(res.data.enabled_post_types || {})
            snapshotRef.current = settingsSnapshot(built)
            setIsDataLoaded(true)
        }).catch(() => {
            toast.error("Couldn't load notification settings")
            setIsDataLoaded(true)
        })
    }, [])

    // ── Change detection ─────────────────────────────────────────────────────
    useEffect(() => {
        if (snapshotRef.current === null) return
        setHasChanges(settingsSnapshot(settings) !== snapshotRef.current)
    }, [settings, setHasChanges])

    const handleCardChange = (type, nextSetting) => {
        setSettings(prev => ({ ...prev, [type]: nextSetting }))
    }

    const onSubmit = useCallback(() => {
        const invalid = findInvalidMentionedFor(settings)
        if (invalid) {
            toast.error("Select at least one mention category, or turn off the filter.")
            return Promise.reject(new Error("invalid_mentioned_for"))
        }
        return apiAgent({
            method: 'post',
            url: '/users/update_notifications',
            data: { notification_settings: toApiPayload(settings) }
        }).then(() => {
            toast.success("Changes saved")
            snapshotRef.current = settingsSnapshot(settings)
            setHasChanges(false)
        }).catch(() => {
            toast.error("Failed to save changes")
        })
    }, [settings, toast, setHasChanges])

    const handleCancel = useCallback(() => {
        if (snapshotRef.current) setSettings(JSON.parse(snapshotRef.current))
    }, [])

    const submitRef = useRef(onSubmit)
    const cancelRef = useRef(handleCancel)
    submitRef.current = onSubmit
    cancelRef.current = handleCancel

    useEffect(() => {
        const id = registerTab({
            onSave: (...args) => submitRef.current(...args),
            onCancel: (...args) => cancelRef.current(...args),
        })
        return () => unregisterTab(id)
    }, [registerTab, unregisterTab])

    // SEC access is required even if the notification capability is missing.
    const visibleTypes = NOTIFICATION_POST_TYPES.filter(({ type }) => {
        if (type === "Post::SecFiling") return current_team?.sec_filings_enabled
        return !(type in enabledTypes) || enabledTypes[type]
    })

    return (
        <div className="settings-panel">
            <div className="settings-panel__header">
                <div className="settings-panel__title body-l-bold">Notifications</div>
                <div className="settings-panel__description body-s-semibold">Control how and when you receive alerts, updates, and activity digests across all data types.</div>
            </div>

            {!isDataLoaded ? <Loader isLoading Size="Medium" /> :
                <div className="ntf-cards">
                    {visibleTypes.map(tc => (
                        <NotificationTypeCard
                            key={tc.type}
                            typeConfig={tc}
                            setting={settings[tc.type]}
                            mentionedForTags={mentionedForTags[tc.type] || []}
                            expanded={expandedType === tc.type}
                            onToggle={() => setExpandedType(expandedType === tc.type ? null : tc.type)}
                            onChange={(s) => handleCardChange(tc.type, s)}
                            groupKey="me"
                        />
                    ))}
                </div>}
        </div>
    )
}

export default ManageNotifications
